import { LoadingScreen } from '@avanta/shared/components/LoadingScreen';
import { Text } from '@avanta/shared/components/Text';
import { Card } from '@avanta/shared/components/ui/Card';
import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { getStatusBadge, isUpcomingAppointment } from '@avanta/shared/utils/appointments';
import { useTranslation } from 'react-i18next';
import { useFetchActivePatientRecord } from 'hooks/useFetchActivePatientRecord';
import { useFetchNextAppointments } from 'hooks/useFetchNextAppointments';
import { type Appointment } from 'types/appointments';
import { CardActions } from './components/CardActions';
import { PsychologistInfo } from './components/PsychologistInfo';
import { SessionDetailsSection } from './components/SessionDetailsSection';

export const AppointmentCard = ({
  patientId,
  appointment: appointmentFromProps,
}: {
  patientId: string;
  appointment?: Appointment;
}): React.ReactElement | null => {
  const { t } = useTranslation(LOCIZE_NS.INSURANCE);
  const appointmentId = appointmentFromProps?.id;
  const { data: nextAppointments, isPending } = useFetchNextAppointments(
    patientId,
    appointmentFromProps?.psychologistId
  );
  const { data: patientRecord } = useFetchActivePatientRecord(patientId);

  const isInitialLoading = isPending && !nextAppointments;

  if (isInitialLoading) {
    return <LoadingScreen />;
  }

  const appointment = appointmentId
    ? nextAppointments?.find(item => item.id === appointmentId)
    : nextAppointments?.find(isUpcomingAppointment);

  if (!appointment || appointment.canceled) {
    return null;
  }

  return (
    <Card className="relative">
      <div className="absolute top-4 right-8">{getStatusBadge(appointment)}</div>
      <div className="flex flex-col gap-6 p-2">
        <div className="flex flex-col gap-1">
          <Text variant="heading-sm-medium" className="text-denim-500">
            {t('AppointmentCard.details.heading')}
          </Text>
          <Text variant="text-sm-regular" className="text-gray-400" tag="span">
            {t('AppointmentCard.details.recordNumber.label')}
            <Text variant="text-sm-regular" className="ml-1 text-denim-800" tag="span">
              {patientRecord?.code}
            </Text>
          </Text>
        </div>
        <div className="flex justify-between items-center">
          <SessionDetailsSection appointment={appointment} />
          <PsychologistInfo appointment={appointment} />
        </div>
        <CardActions appointment={appointment} />
      </div>
    </Card>
  );
};
